import { useLayoutEffect, useRef, useState } from 'react';

/* eslint-disable lingui/no-unlocalized-strings */
const EXPAND_TRANSITION = 'height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease';
const COLLAPSE_TRANSITION =
  'height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease';
const NO_TRANSITION = 'none';
/* eslint-enable lingui/no-unlocalized-strings */

/**
 * Drives the height/opacity expand-collapse animation for a collapsible section.
 * Returns the style to apply to the clipping container and a ref to attach to the
 * inner content wrapper (used to measure target height).
 */
export function useCollapseAnimation(isOpen: boolean) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    height: isOpen ? 'auto' : 0,
    opacity: isOpen ? 1 : 0,
    overflow: 'hidden',
  });
  const isFirstRender = useRef(true);
  const animationFrame = useRef<number>();

  useLayoutEffect(() => {
    // On first render, just set the initial state without animation
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setStyle({
        height: isOpen ? 'auto' : 0,
        opacity: isOpen ? 1 : 0,
        overflow: isOpen ? undefined : 'hidden',
      });
      return;
    }

    const inner = innerRef.current;
    if (!inner) return;

    // Cancel any in-flight animation frame
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }

    if (isOpen) {
      // Expanding: measure target height, animate from 0 to measured height
      const targetHeight = inner.scrollHeight;

      // Start from collapsed
      setStyle({
        height: 0,
        opacity: 0,
        overflow: 'hidden',
        transition: NO_TRANSITION,
      });

      // Next frame: animate to full height
      animationFrame.current = requestAnimationFrame(() => {
        setStyle({
          height: targetHeight,
          opacity: 1,
          overflow: 'hidden',
          transition: EXPAND_TRANSITION,
        });

        // After animation completes, switch to auto height for dynamic content
        const timer = setTimeout(() => {
          setStyle({
            height: 'auto',
            opacity: 1,
            overflow: undefined,
            transition: undefined,
          });
        }, 260);

        return () => clearTimeout(timer);
      });
    } else {
      // Collapsing: measure current height, then animate to 0
      const currentHeight = inner.scrollHeight;

      // Pin to current measured height
      setStyle({
        height: currentHeight,
        opacity: 1,
        overflow: 'hidden',
        transition: NO_TRANSITION,
      });

      // Next frame: animate to 0
      animationFrame.current = requestAnimationFrame(() => {
        setStyle({
          height: 0,
          opacity: 0,
          overflow: 'hidden',
          transition: COLLAPSE_TRANSITION,
        });
      });
    }

    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [isOpen]);

  return { style, innerRef };
}
